import {
  IconDots,
  IconSquareX,
  IconWindowMaximize,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { ActionIcon, Menu } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { SuperBarViewState, useChatContext } from './context/use-chat-context';
import { useGetStoreInfo } from './hooks/useGetStoreInfo';
import { useGetProductId } from './hooks/useGetProductId';
import { useAnalytics } from './hooks/useAnalytics';
import { AnalyticsEvent } from './constants/analyticsEvents';
import { appendSbRef } from './utils/append-sb-ref';
import {
  SuperbarFeedback,
  SuperbarDark,
} from '@caddie/ui-kit/components/icons';
import { Tooltip } from '@caddie/ui-kit/components/tooltip';
import { CADDIE_Z_INDEX } from '../constants/z-index';
import { useSuperbarTheme } from './hooks/useSuperbarTheme';
import { cn } from './utils/styles';

const SUPERBAR_URL =
  'http://www.superbar.ai/?utm_source=product&utm_medium=active-superbar';

type SuperbarMenuProps = {
  onClear: () => void;
};

export const SuperbarMenu = ({ onClear }: SuperbarMenuProps) => {
  const theme = useSuperbarTheme();
  const { superBarViewState, setSuperBarViewState, isMenuOpen, setIsMenuOpen } =
    useChatContext();
  const { trackEvent } = useAnalytics();

  const isMobile = useMediaQuery('(max-width: 767px)');

  const handleExpandBar = () => {
    setSuperBarViewState(SuperBarViewState.EXPANDED);
  };

  const handleOpenBar = () => {
    setSuperBarViewState(SuperBarViewState.DEFAULT);
  };

  const handleCloseBar = () => {
    setSuperBarViewState(SuperBarViewState.CLOSED);
  };

  const { store, currentSession } = useGetStoreInfo();
  const productId = useGetProductId(store?.config?.config);
  const storeConfig = store?.config?.config;
  const shouldHidePoweredBy = storeConfig?.hidePoweredBySuperbar ?? false;

  const getFeedbackUrl = () => {
    const url = window.location.href;
    return `https://airtable.com/appPFpiG5TKFxReLa/pagAekk8WFNR3RzRM/form?prefill_Store=${encodeURIComponent(store?.name || '')}&prefill_Store+ID=${encodeURIComponent(store?.id || '')}&prefill_Product+ID=${encodeURIComponent(productId || '')}&prefill_Session+ID=${encodeURIComponent(currentSession?.id || '')}&prefill_URL=${encodeURIComponent(url)}&hide_Session+ID=true&hide_Store=true&hide_URL=true&hide_Store+ID=true&hide_Product+ID=true`;
  };
  const handlePoweredBySuperbarClick = () => {
    const urlWithRef = appendSbRef(SUPERBAR_URL);

    trackEvent(AnalyticsEvent.POWERED_BY_SUPERBAR_CLICK, {
      sb_ref_host: window.location.host,
      dest_url: urlWithRef,
      page_origin: window.location.origin,
    });
    window.open(urlWithRef, '_blank');
  };

  const mobileMenuStyles = isMobile
    ? {
        left: '0',
        right: '0',
        width: isMobile ? '100%' : 'max-content',
      }
    : {};

  const ICON_COLOR = theme.colors.ink.disabled;
  const TEXT_COLOR = theme.colors.ink.primary;

  const menuIconColor = theme.components.menu.iconColor;

  return (
    <Menu
      shadow="md"
      position={'top'}
      withinPortal
      opened={isMenuOpen}
      onOpen={() => setIsMenuOpen(true)}
      onClose={() => setIsMenuOpen(false)}
      styles={{
        dropdown: {
          zIndex: CADDIE_Z_INDEX.MENU,
          ...mobileMenuStyles,
        },
      }}
    >
      <Menu.Target>
        <Tooltip
          label="Menu"
          position="top"
          withArrow
          zIndex={CADDIE_Z_INDEX.MENU}
          disabled={isMenuOpen}
        >
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            radius="xl"
            aria-label="Menu"
            className="hover:caddietw-bg-black/5 caddietw-transition-colors"
          >
            <IconDots color={menuIconColor} />
          </ActionIcon>
        </Tooltip>
      </Menu.Target>

      <Menu.Dropdown
        bg={theme.components.assistantMessage.closeButton.background}
        className={theme.components.menu.className}
        style={{
          border: `1px solid ${theme.components.assistantMessage.closeButton.borderColor}`,
          borderRadius: isMobile ? '24px 24px 0 0' : '4px',
          margin: isMobile ? '0 16px' : '0',
          width: isMobile ? 'calc(100% - 32px)' : 'max-content',
        }}
      >
        {isMobile && (
          <div
            onClick={() => setIsMenuOpen(false)}
            className={cn(
              'caddietw-absolute caddietw-top-[-6px] caddietw-right-[-6px] caddietw-z-10 caddietw-cursor-pointer caddietw-flex caddietw-w-6 caddietw-h-6 caddietw-justify-center caddietw-items-center caddietw-shrink-0 caddietw-outline-none focus:caddietw-outline-none focus-visible:caddietw-outline-none hover:caddietw-bg-gray-600',
              'caddietw-border caddietw-border-solid',
              'caddietw-transition-all caddietw-duration-200',
              theme.components.menu.closeButton.className
            )}
            style={{
              borderRadius: theme.radii.round,
              borderColor: theme.components.menu.closeButton.borderColor,
              background: theme.components.menu.closeButton.background,
              boxShadow: theme.shadows.systemMessageCloseButton,
            }}
          >
            <IconX
              size={'12px'}
              strokeWidth={4}
              style={{
                color: theme.components.assistantMessage.closeButton.icon,
                transition: 'color 200ms ease-in-out',
              }}
            />
          </div>
        )}

        <Menu.Item
          leftSection={
            <SuperbarDark
              width={18}
              height={18}
              stroke={theme.components.menu.poweredBy.iconBgColor}
              logoColor={theme.components.menu.poweredBy.iconColor}
            />
          }
          onClick={handlePoweredBySuperbarClick}
          style={{
            color: TEXT_COLOR,
            '--menu-item-hover-bg': theme.components.menu.itemHoverBackground,
          }}
          className="hover:!caddietw-bg-[var(--menu-item-hover-bg)]"
        >
          Powered by <b>Superbar</b>
        </Menu.Item>
        {!shouldHidePoweredBy && (
          <>
            <Menu.Divider
              style={{
                width: isMobile ? '100%' : '200px',
                margin: '0 auto',
                borderColor: theme.components.menu.poweredBy.dividerColor,
              }}
            />
          </>
        )}
        {superBarViewState !== SuperBarViewState.CLOSED && (
          <Menu.Item
            leftSection={<IconSquareX size={18} color={ICON_COLOR} />}
            onClick={handleCloseBar}
            style={{
              color: TEXT_COLOR,
              '--menu-item-hover-bg': theme.components.menu.itemHoverBackground,
            }}
            className="hover:!caddietw-bg-[var(--menu-item-hover-bg)]"
          >
            Close Superbar
          </Menu.Item>
        )}
        {superBarViewState === SuperBarViewState.CLOSED && (
          <Menu.Item
            leftSection={<IconWindowMaximize size={18} color={ICON_COLOR} />}
            onClick={handleOpenBar}
            style={{
              color: TEXT_COLOR,
              '--menu-item-hover-bg': theme.components.menu.itemHoverBackground,
            }}
            className="hover:!caddietw-bg-[var(--menu-item-hover-bg)]"
          >
            Open Superbar
          </Menu.Item>
        )}
        <Menu.Item
          leftSection={
            <SuperbarFeedback width={18} height={18} stroke={ICON_COLOR} />
          }
          onClick={() => {
            window.open(getFeedbackUrl(), '_blank');
          }}
          style={{
            color: TEXT_COLOR,
            '--menu-item-hover-bg': theme.components.menu.itemHoverBackground,
          }}
          className="hover:!caddietw-bg-[var(--menu-item-hover-bg)]"
        >
          Share Feedback
        </Menu.Item>
        <Menu.Item
          leftSection={<IconTrash size={18} color={ICON_COLOR} />}
          onClick={onClear}
          style={{
            color: TEXT_COLOR,
            '--menu-item-hover-bg': theme.components.menu.itemHoverBackground,
          }}
          className="hover:!caddietw-bg-[var(--menu-item-hover-bg)]"
        >
          Clear Search
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};
